/* eslint-disable max-lines-per-function */
import { useContext, useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { IAlert } from "../../interfaces/alert";
import { IValidation } from "../../interfaces/form";
import {
  DATE_FORMAT,
  ERROR,
  ERROR_FROM_GREATER_THAN_TO,
  ERROR_TO_GREATER_THAN_FROM,
  ERRORS_MESSAGES,
  GET,
  INFO,
  INTERNAL,
  LOCALE,
  MARKETPLACE,
  MEDIUM,
  POST,
} from "../../utils/constants";
import { fetchData } from "../../utils/dataProvider";
import { validateInputs } from "../../utils/inputValidations";
import {
  compareDates,
  downloadFromURL,
  mongoTimeStampToString,
} from "../../utils/orders";
import Loader from "../../components/Loader";
import GenericAlert from "../../components/GenericAlert";
import ListHeader from "../../components/tables/ListHeader";
import { UserContext } from "../../contexts/userContext";

interface IQuoteAuditExportFilters {
  fromDate?: string;
  toDate?: string;
  clientId?: number;
  clientName?: string;
  merchantId?: number;
  merchantName?: string;
}

interface IQuoteAuditExportFiltersErrors {
  fromDate?: string[];
  toDate?: string[];
  invalidFrom?: boolean;
  invalidTo?: boolean;
}

interface IQuoteAuditExportRecord {
  createdAt: string;
  status: string;
  path?: string;
  filter: {
    from: string;
    to: string;
    client_id?: number;
    allResponsesFailed?: boolean;
    client_name?: string;
    merchant_id?: number;
  };
}

interface IClient {
  id: number;
  name: string;
}

const { fromDateRequired, toDateRequired } = ERRORS_MESSAGES;

const formValidations: IValidation[] = [
  { id: "fromDate", type: "required", message: fromDateRequired },
  { id: "toDate", type: "required", message: toDateRequired },
  {
    id: "fromDate",
    type: "compare-dates",
    message: ERROR_FROM_GREATER_THAN_TO,
    param: "toDate",
  },
];

const tableColumns = [
  { label: "Fecha de creación" },
  { label: "Desde" },
  { label: "Hasta" },
  { label: "Marketplace" },
  { label: "Merchant" },
  { label: "Estado" },
  { label: "Acciones" },
];

const showInputErrors = (inputErrors: string[]) =>
  inputErrors.map((message: string) => (
    <FormHelperText className="color-error" key={message}>
      {message}
    </FormHelperText>
  ));

const ExportQuoteAudits = () => {
  const {
    currentUser: { entity, clientId: loggedUserClientId },
  } = useContext(UserContext);
  const isInternalUser = entity === INTERNAL;
  const [values, setValues] = useState<IQuoteAuditExportFilters>({});
  const [errors, setErrors] = useState<IQuoteAuditExportFiltersErrors>({});
  const [alert, setAlert] = useState<IAlert>({} as IAlert);
  const [tableAlert, setTableAlert] = useState<IAlert>({} as IAlert);
  const [requests, setRequests] = useState<IQuoteAuditExportRecord[]>([]);
  const [clients, setClients] = useState<IClient[]>([]);
  const [merchants, setMerchants] = useState<IClient[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchExportRecords = async () => {
    try {
      const url = isInternalUser
        ? "/internal/files?type=quote_audit_export"
        : "/orders/files?type=quote_audit_export";
      const result = await fetchData({
        url,
        method: GET,
      });
      let parsedResults: IQuoteAuditExportRecord[] = [];
      if (Array.isArray(result)) parsedResults = result;
      if (!Array.isArray(result)) {
        const { data } = result;
        if (Array.isArray(data)) parsedResults = data;
      }
      setRequests(parsedResults);
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    }
  };

  const fetchClients = async () => {
    try {
      const { clients: fetchedClients } = await fetchData({
        url: "/internal/clients/accepted/?skip=0&limit=0",
        method: GET,
      });
      setClients(fetchedClients || []);
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    }
  };

  const fetchMerchants = async () => {
    if (entity !== MARKETPLACE) return;

    try {
      const { integrations } = await fetchData({
        url: "/marketplaces_merchants/acceptedIntegrationsOfMarketplace",
        method: GET,
      });
      const parsedMerchants = (integrations || []).map(
        ({
          merchant,
        }: {
          merchant: { id: number; entity: { name: string } };
        }) => ({
          id: merchant.id,
          name: merchant.entity.name,
        }),
      );
      setMerchants(parsedMerchants);
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    }
  };

  useEffect(() => {
    const getExtraFetches = () => {
      if (isInternalUser) return [fetchClients()];
      if (entity === MARKETPLACE) return [fetchMerchants()];
      return [];
    };

    const init = async () => {
      await Promise.all([fetchExportRecords(), ...getExtraFetches()]);
      setLoading(false);
    };
    init();
  }, []);

  const validateDates = (keyName: string, date: string | null) => {
    const { fromDate, toDate } = values;
    const isDateValid = compareDates(
      keyName === "fromDate" ? date : fromDate,
      keyName === "fromDate" ? toDate : date,
    );
    setErrors({
      ...errors,
      invalidFrom: keyName === "fromDate" && !isDateValid,
      invalidTo: keyName === "toDate" && !isDateValid,
    });
  };

  const handleChangeDate = (dateType: string, newValue: any) => {
    validateDates(dateType, newValue);
    setValues({ ...values, [dateType]: newValue });
  };

  const handleSubmit = async () => {
    const inputErrors = validateInputs(values, formValidations);
    setErrors(inputErrors);

    if (Object.keys(inputErrors).length === 0) {
      setAlert({} as IAlert);
      setTableAlert({
        typeOfAlert: INFO,
        message: "El archivo se está procesando.",
        showAlert: true,
      });

      try {
        const { fromDate, toDate, clientId, merchantId } = values;
        const body: Record<string, any> = {
          fromDate,
          toDate,
          allResponsesFailed: true,
        };
        const resolvedClientId =
          clientId ?? (isInternalUser ? undefined : loggedUserClientId);
        if (resolvedClientId !== undefined) body.clientId = resolvedClientId;
        if (merchantId) body.merchantId = merchantId;

        await fetchData({ url: "/quotes/audits/export", method: POST, body });

        setValues({});
        setTableAlert({} as IAlert);
        await fetchExportRecords();
      } catch (error: any) {
        setTableAlert({} as IAlert);
        setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
      }
    }
  };

  if (loading) return <Loader size={MEDIUM} />;

  return (
    <Grid container>
      <Grid
        item
        xs={12}
        md={7}
        display="flex"
        alignItems="center"
        justifyContent="start"
      >
        <Typography className="f-s-20 text-dark" ml={1}>
          Exportar auditorías de cotizaciones
        </Typography>
      </Grid>

      <Grid container spacing={2} className="m-t-10">
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={LOCALE}>
          <Grid item xs={12} md={2}>
            <DatePicker
              label="Desde"
              value={values.fromDate || null}
              onChange={(newValue: any) => {
                if (!newValue?.invalid) handleChangeDate("fromDate", newValue);
              }}
              inputFormat={DATE_FORMAT}
              renderInput={(params: any) => (
                <TextField
                  fullWidth
                  size="small"
                  {...params}
                  error={errors.invalidFrom}
                />
              )}
              disableFuture
            />
            <FormHelperText className="color-error" key="error-from">
              {errors.fromDate &&
                errors.fromDate.length > 0 &&
                showInputErrors(errors.fromDate)}
              {errors.invalidFrom && ERROR_FROM_GREATER_THAN_TO}
            </FormHelperText>
          </Grid>
          <Grid item xs={12} md={2}>
            <DatePicker
              label="Hasta"
              value={values.toDate || null}
              onChange={(newValue: any) => {
                if (!newValue?.invalid) handleChangeDate("toDate", newValue);
              }}
              inputFormat={DATE_FORMAT}
              renderInput={(params: any) => (
                <TextField
                  fullWidth
                  size="small"
                  {...params}
                  error={errors.invalidTo}
                />
              )}
              disableFuture
            />
            <FormHelperText className="color-error" key="error-to">
              {errors.toDate &&
                errors.toDate.length > 0 &&
                showInputErrors(errors.toDate)}
              {errors.invalidTo && ERROR_TO_GREATER_THAN_FROM}
            </FormHelperText>
          </Grid>
        </LocalizationProvider>

        {isInternalUser && clients.length > 0 && (
          <Grid item xs={12} md={2}>
            <Autocomplete
              inputValue={values.clientName || ""}
              getOptionLabel={(option) => option.name}
              options={clients}
              noOptionsText="No se encontró el cliente"
              onChange={(_, value) =>
                setValues({
                  ...values,
                  clientId: value ? Number(value.id) : undefined,
                  clientName: value?.name || "",
                })
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth size="small" label="Cliente" />
              )}
            />
          </Grid>
        )}

        {merchants.length > 0 && (
          <Grid item xs={12} md={2}>
            <Autocomplete
              inputValue={values.merchantName || ""}
              getOptionLabel={(option) => option.name}
              options={merchants}
              noOptionsText="No se encontró el merchant"
              onChange={(_, value) =>
                setValues({
                  ...values,
                  merchantId: value ? Number(value.id) : undefined,
                  merchantName: value?.name || "",
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label="Merchant"
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12} md={2} display="flex" alignItems="center">
          <FormControlLabel
            control={<Checkbox checked disabled />}
            label="Fallo general"
          />
        </Grid>

        <Grid item xs={12} md={2} sx={{ ml: { md: "auto" } }}>
          <Button
            fullWidth
            variant="contained"
            size="small"
            sx={{ padding: "8px" }}
            onClick={handleSubmit}
          >
            Generar archivo
          </Button>
        </Grid>
      </Grid>

      {alert.showAlert && (
        <Grid item xs={12} className="m-t-10">
          <GenericAlert alert={alert} />
        </Grid>
      )}

      {tableAlert.showAlert && (
        <Grid item xs={12} className="m-t-10">
          <GenericAlert alert={tableAlert} />
        </Grid>
      )}

      <TableContainer component={Paper} className="m-t-40 br-20 bg-light">
        <Table>
          <ListHeader columns={tableColumns} />
          <TableBody>
            {requests.map((record: IQuoteAuditExportRecord, index: number) => (
              <TableRow key={index}>
                <TableCell className="p-l-20">
                  {mongoTimeStampToString(record.createdAt, true)}
                </TableCell>
                <TableCell>
                  {mongoTimeStampToString(record.filter?.from, true)}
                </TableCell>
                <TableCell>
                  {mongoTimeStampToString(record.filter?.to, true)}
                </TableCell>
                <TableCell>
                  {clients.find(
                    (client) => client.id === record.filter?.client_id,
                  )?.name || "Todos"}
                </TableCell>
                <TableCell>
                  {merchants?.find(
                    (merchant) => merchant.id === record.filter?.merchant_id,
                  )?.name || "Todos"}
                </TableCell>
                <TableCell>{record.status}</TableCell>
                <TableCell>
                  {record.path ? (
                    <Tooltip arrow title="Descargar" placement="top">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => downloadFromURL(record.path as string)}
                      >
                        <span className="material-symbols-rounded">
                          download
                        </span>
                      </IconButton>
                    </Tooltip>
                  ) : (
                    record.status
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

export default ExportQuoteAudits;
